import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URLS } from '../config/api';
import { apiPost, apiDelete } from '../utils/apiUtils';
import './StaticScenesCategory.css';

const StaticScenesCategory = ({ novelId, novel, setNovel, refreshNovel, isProduction = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(0);

  const handleCreateScene = async (event) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new FormData(event.target);
    const name = formData.get('name')?.trim();
    const tag = formData.get('tag')?.trim() || '';

    if (!name) {
      setError('Название сцены обязательно');
      setLoading(false);
      return;
    }

    try {
      const requestData = { name, tag };
      const response = await apiPost(
        isProduction ? API_URLS.PRODUCTION_STATIC_SCENES(novelId) : API_URLS.BUILDING_STATIC_SCENES(novelId),
        requestData,
        { headers: { 'Content-Type': 'application/json' } },
        true,
        'Статическая сцена создана успешно!'
      );

      setShowCreateForm(false);

      // Refresh novel data to ensure consistency across all components
      await refreshNovel();
    } catch (err) {
      // Error is already shown by toast notification
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteScene = async (sceneId) => {
    try {
      setLoading(true);
      const response = await apiDelete(
        isProduction ? API_URLS.PRODUCTION_STATIC_SCENE(novelId, sceneId) : API_URLS.BUILDING_STATIC_SCENE(novelId, sceneId),
        {},
        true,
        'Статическая сцена удалена успешно!'
      );

      // Refresh novel data to ensure consistency across all components
      await refreshNovel();
      setDeleteConfirm(null);
    } catch (err) {
      // Error is already shown by toast notification
    } finally {
      setLoading(false);
    }
  };

  const handleEditScene = (sceneId) => {
    // Navigate to the static scene detail page
    const basePath = isProduction ? 'production_change' : 'building';
    navigate(`/${basePath}/${novelId}/static_scene/${sceneId}`);
  };

  // Get unique tags from scenes (split by spaces)
  const allTags = [...new Set((novel?.static_scenes || [])
    .filter(scene => scene && scene.tag)
    .flatMap(scene => scene.tag.split(/\s+/).filter(tag => tag.trim()))
  )];

  // Filter scenes based on search term and selected tags
  const filtered = (novel?.static_scenes || []).filter(scene => {
    const matchesSearch = scene && scene.name && scene.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTags = selectedTags.length === 0 ||
      (scene.tag && selectedTags.every(selectedTag =>
        scene.tag.split(/\s+/).includes(selectedTag)
      ));
    return matchesSearch && matchesTags;
  });

  // Reset page on filter change
  useEffect(() => { setPage(0); }, [searchTerm, selectedTags]);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div className="ssc-wrap">
      {/* Toolbar: Search + Add Button */}
      <div className="ssc-toolbar">
        <input
          type="text"
          placeholder="Поиск сцен..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="ssc-search"
        />
        {(searchTerm || selectedTags.length > 0) && (
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', fontFamily: "'Raleway', sans-serif" }}>
            {filtered.length} {'сцен найдено'}
          </span>
        )}
        <button
          className="ssc-add-btn"
          onClick={() => setShowCreateForm(true)}
          disabled={loading}
        >
          + {'Добавить статическую сцену'}
        </button>
      </div>

      {error && (
        <div className="ssc-error">
          {error}
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <div className="ssc-overlay">
          <div className="ssc-modal">
            <h3 className="ssc-modal-title">{'Добавить статическую сцену'}</h3>
            <form onSubmit={handleCreateScene}>
              <div className="ssc-form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Название сцены"
                  required
                  disabled={loading}
                  className="ssc-input"
                  autoFocus
                />
              </div>
              <div className="ssc-form-group">
                <input
                  type="text"
                  name="tag"
                  placeholder="Тег (необязательно)"
                  disabled={loading}
                  className="ssc-input"
                />
              </div>

              <div className="ssc-modal-actions">
                <button
                  type="submit"
                  disabled={loading}
                  className="ssc-btn-submit"
                >
                  {loading ? 'Загрузка...' : 'Создать'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setError(null);
                  }}
                  disabled={loading}
                  className="ssc-btn-cancel"
                >
                  {'Отмена'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tag Filter */}
      {allTags.length > 0 && (
        <div className="ssc-tags">
          <button
            className={`ssc-tag ${selectedTags.length === 0 ? 'active' : ''}`}
            onClick={() => setSelectedTags([])}
          >
            {'Все'}
          </button>
          {allTags.map(tag => (
            <button
              key={tag}
              className={`ssc-tag ${selectedTags.includes(tag) ? 'active' : ''}`}
              onClick={() => {
                if (selectedTags.includes(tag)) {
                  setSelectedTags(selectedTags.filter(t => t !== tag));
                } else {
                  setSelectedTags([...selectedTags, tag]);
                }
              }}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      {/* Static Scenes List */}
      <div className="ssc-list">
        {paged.length > 0 ? (
          paged.map((scene) => (
            <div key={scene.id} className="ssc-card">
              <div className="ssc-card-head">
                <h4 className="ssc-card-name">{scene.name}</h4>
                <div className="ssc-card-actions">
                  {scene.tag && (
                    <span className="ssc-card-tag">{scene.tag}</span>
                  )}
                  <button
                    className="ssc-btn-edit"
                    onClick={() => handleEditScene(scene.id)}
                    disabled={loading}
                  >
                    {'Редактировать'}
                  </button>
                  <button
                    className="ssc-btn-delete"
                    onClick={() => setDeleteConfirm(scene.id)}
                    disabled={loading}
                  >
                    {'Удалить'}
                  </button>
                </div>
              </div>
              {scene.original_message && (
                <p className="ssc-card-msg">
                  {scene.original_message.length > 100
                    ? scene.original_message.substring(0, 100) + '...'
                    : scene.original_message}
                </p>
              )}
            </div>
          ))
        ) : (
          <div className="ssc-empty">
            <p>{'Статические сцены не найдены'}</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="ssc-pagination">
          <button className="ssc-page-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}>← Назад</button>
          <span>Page {page + 1} of {totalPages}</span>
          <button className="ssc-page-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>Вперёд →</button>
        </div>
      )}

      {deleteConfirm && (
        <div className="ssc-overlay">
          <div className="ssc-modal">
            <h3 className="ssc-modal-title">{'Вы уверены, что хотите удалить эту статическую сцену?'}</h3>
            <div className="ssc-modal-actions">
              <button
                className="ssc-btn-cancel"
                onClick={() => setDeleteConfirm(null)}
                disabled={loading}
              >
                {'Отмена'}
              </button>
              <button
                className="ssc-btn-danger"
                onClick={() => handleDeleteScene(deleteConfirm)}
                disabled={loading}
              >
                {loading ? 'Удаление...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaticScenesCategory;
